import {
  getCustomerOrderLineCancelQty,
  getCustomerOrderLineDiscountRate,
  getCustomerOrderLineOrderQty,
  getCustomerOrderLineRemainingQty,
  getCustomerOrderLineUnitPrice,
  getExtraItemReferenceQty,
} from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';
import { mapCustomerOrderTaxTypeToSalesOrderTaxMode } from '#/modules/crmMgmt/customerOrder/schemas/customerOrderSchema';
import {
  pickDefaultCustomerAddressId,
  pickDefaultCustomerContactId,
} from '#/modules/crmMgmt/customer/helper/customerAddress';

function resolveDefaultLineQty(line) {
  const remaining = Number(getCustomerOrderLineRemainingQty(line));
  if (Number.isFinite(remaining) && remaining > 0) {
    return remaining;
  }
  const orderQty = Number(getCustomerOrderLineOrderQty(line));
  const cancelQty = Number(getCustomerOrderLineCancelQty(line));
  if (Number.isFinite(orderQty)) {
    const cancel = Number.isFinite(cancelQty) ? cancelQty : 0;
    return Math.max(orderQty - cancel, 0);
  }
  return '';
}

function mapExtraItemFromCustomerOrderLine(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }
  return {
    customerOrderExtraItemSnapshotId: item.id ?? null,
    itemName: item.itemName ?? '',
    actualQty: getExtraItemReferenceQty(item) ?? item.actualQty ?? '',
    unitName: item.unitName ?? '',
    remark: item.remark ?? '',
  };
}

/**
 * 客訂與銷貨表單折數同為價格倍率（0.9＝九折；getter 已自後端折抵比例轉換）。原值抄過去；空白維持空白。
 */
export function mapCustomerOrderDiscountRateToSalesOrderMultiplier(coRate) {
  if (coRate === '' || coRate == null) {
    return '';
  }
  const rate = Number(coRate);
  if (!Number.isFinite(rate)) {
    return '';
  }
  return rate;
}

/**
 * Customer detail → 銷貨單地址／聯絡預填（三 ID）。
 * @param {object | null | undefined} customerDetail
 */
export function mapCustomerDetailToSalesOrderShippingPrefill(customerDetail) {
  return {
    billingAddressId: pickDefaultCustomerAddressId(
      customerDetail?.addresses,
      'BILLING',
    ),
    shippingAddressId: pickDefaultCustomerAddressId(
      customerDetail?.addresses,
      'SHIPPING',
    ),
    shippingContactId: pickDefaultCustomerContactId(customerDetail?.contacts),
  };
}

/**
 * 客訂明細列 → 建 SO 草稿列（UI／POST 共用；不送 amounts）。
 */
export function mapCustomerOrderLineToSalesOrderCreateLine(line) {
  if (!line) {
    return null;
  }
  return {
    customerOrderLineId: line.id ?? null,
    productId: line.product?.id ?? line.productId ?? null,
    product: line.product ?? null,
    qty: resolveDefaultLineQty(line),
    unitPrice: getCustomerOrderLineUnitPrice(line) ?? '',
    discountRate: mapCustomerOrderDiscountRateToSalesOrderMultiplier(
      getCustomerOrderLineDiscountRate(line),
    ),
    remark: line.remark ?? '',
    invAllocations: [],
    extraItems: (line.extraItems ?? [])
      .map(mapExtraItemFromCustomerOrderLine)
      .filter(Boolean),
  };
}

/**
 * 客訂其他產品列 → 建 SO `manualLines`（完整 snapshot；不進 lines[]）。
 */
export function mapCustomerOrderManualLineToSalesOrderCreateManualLine(line) {
  if (!line) {
    return null;
  }
  return {
    customerOrderManualLineId: line.id ?? null,
    itemName: line.itemName ?? '',
    qty: resolveDefaultLineQty(line),
    unitName: line.unitName ?? '',
    unitPrice: getCustomerOrderLineUnitPrice(line) ?? '',
    remark: line.remark ?? '',
  };
}
/**
 * 客訂 Detail → 建銷貨單 full DRAFT 預填（畫面參考；後端不自動帶）。
 * 稅別：CO `taxType` → SO `taxMode`。
 * 地址／聯絡：CO header／snapshot 三 ID。
 */
export function mapCustomerOrderDetailToSalesOrderCreateDraft(detail) {
  const snapshot = detail?.customerSnapshot;

  return {
    plannedShipDate: '',
    billingAddressId: detail?.billingAddressId
      ?? snapshot?.billingAddress?.id
      ?? '',
    shippingAddressId: detail?.shippingAddressId
      ?? snapshot?.shippingAddress?.id
      ?? '',
    shippingContactId: detail?.shippingContactId
      ?? snapshot?.shippingContact?.id
      ?? '',
    currencyId: '',
    taxMode: mapCustomerOrderTaxTypeToSalesOrderTaxMode(detail?.taxType),
    taxRate: detail?.taxRate ?? 0.05,
    roundingAdjustment: detail?.roundingAdjustment ?? 0,
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    customerId: detail?.customerId ?? detail?.customer?.id ?? '',
    lines: (detail?.lines ?? [])
      .map(mapCustomerOrderLineToSalesOrderCreateLine)
      .filter(Boolean),
    manualLines: (detail?.manualLines ?? [])
      .map(mapCustomerOrderManualLineToSalesOrderCreateManualLine)
      .filter(Boolean),
  };
}
